import type {
  ContactDetails,
  QuestionnaireAnswers,
  QuestionnaireQuestion,
  QuestionnaireQuestionId,
} from "../types/questionnaire";

export const questionnaireQuestions = [
  {
    id: "goal",
    type: "single",
    title: "¿Qué te gustaría conseguir con Evolution?",
    required: true,
    allowOther: true,
    options: [
      "Perder grasa y mejorar mi composición corporal",
      "Ganar masa muscular",
      "Mejorar mi alimentación y mis hábitos",
      "Mejorar mi rendimiento y entrenamiento",
      "Sentirme mejor conmigo misma",
      "Otro",
    ],
  },
  {
    id: "difficulty",
    type: "single",
    title: "¿Cuál es tu principal dificultad ahora mismo?",
    required: true,
    allowOther: true,
    options: [
      "No sé cómo organizar mi alimentación",
      "Me cuesta ser constante",
      "He probado de todo y nada me funciona",
      "Falta de tiempo",
      "Otro",
    ],
  },
  {
    id: "currentSituation",
    type: "longText",
    title:
      "¿Cómo te hace sentir tu situación actual? Quiero escucharte, cuéntame con tus propias palabras",
    required: true,
    placeholder: "Escribe aquí con total libertad…",
    maxLength: 1_500,
  },
  {
    id: "previousPlans",
    type: "single",
    title: "¿Has probado anteriormente dietas, planes de alimentación o entrenamiento?",
    required: true,
    options: [
      "Sí, varias veces",
      "Sí, pero no me sentí cómoda con el método",
      "No, sería mi primera vez",
    ],
  },
  {
    id: "wellbeingTimeline",
    type: "single",
    title: "¿Desde cuándo sientes que has perdido tu equilibrio o bienestar?",
    required: true,
    options: ["Menos de 3 meses", "Entre 3 y 12 meses", "Más de 1 año"],
  },
  {
    id: "neededSupport",
    type: "single",
    title: "¿Qué crees que necesitas para conseguirlo?",
    required: true,
    options: [
      "Una planificación personalizada",
      "Seguimiento y alguien que me guíe",
      "Aprender a alimentarme y entrenar correctamente",
      "Ayuda para mantener la constancia",
      "Un acompañamiento completo de nutrición + entrenamiento",
      "No estoy segura",
    ],
  },
  {
    id: "professionalExperience",
    type: "single",
    title: "¿Has trabajado anteriormente con un nutricionista o entrenador?",
    required: true,
    options: [
      "Sí, y tuve una buena experiencia",
      "Sí, pero no obtuve los resultados que buscaba",
      "Sí, pero no me sentí acompañada",
      "No, nunca",
    ],
  },
  {
    id: "commitment",
    type: "single",
    title: "¿Cómo describirías tu nivel de compromiso con este cambio?",
    required: true,
    options: [
      "Estoy totalmente preparada para empezar",
      "Estoy comprometida, pero necesito que me guíen",
      "Quiero conseguirlo, pero me cuesta ser constante",
      "Todavía tengo algunas dudas",
    ],
  },
  {
    id: "weeklyTime",
    type: "single",
    title: "¿Cuánto tiempo real puedes dedicar semanalmente a tu proceso?",
    helper:
      "Queremos que el proceso se adapte a tu vida, no que tu vida tenga que adaptarse al proceso.",
    required: true,
    options: ["1–2 horas", "2–4 horas", "4–6 horas", "Más de 6 horas"],
  },
  {
    id: "preferredDuration",
    type: "single",
    title: "¿Qué duración crees que encaja mejor con el cambio que quieres conseguir?",
    required: true,
    options: [
      "2 meses — Quiero empezar y crear una buena base",
      "3 meses — Quiero trabajar en profundidad y conseguir un cambio real",
      "6 meses — Quiero transformar mis hábitos y consolidar resultados",
      "No estoy segura — Prefiero que me recomendéis la duración",
    ],
  },
  {
    id: "supportPriorities",
    type: "single",
    title: "¿Qué es lo que más valoras en un acompañamiento?",
    required: true,
    options: [
      "Sentirme acompañada durante todo el proceso",
      "Tener un plan completamente personalizado",
      "Poder resolver mis dudas cuando lo necesite",
      "Adaptar el plan a mi vida y circunstancias",
      "Ver resultados sin tener que hacer una dieta restrictiva",
      "Todo lo anterior",
    ],
  },
  {
    id: "whyNow",
    type: "longText",
    title: "¿Por qué crees que este es el momento adecuado para empezar?",
    required: true,
    placeholder: "Cuéntame qué ha cambiado para ti…",
    maxLength: 1_500,
  },
  {
    id: "investmentReadiness",
    type: "single",
    title:
      "Mis asesorías 1:1 requieren una inversión de tiempo y dinero en ti. ¿Estás dispuesta a invertir en este momento?",
    required: true,
    options: ["Si Cori! Es mi momento!", "No estoy lista."],
  },
] as const satisfies readonly QuestionnaireQuestion[];

export const QUESTION_COUNT = questionnaireQuestions.length;

export const questionnaireQuestionIds = questionnaireQuestions.map(
  ({ id }) => id,
) as QuestionnaireQuestionId[];

export const createEmptyAnswers = (): QuestionnaireAnswers =>
  questionnaireQuestionIds.reduce<QuestionnaireAnswers>((answers, id) => {
    answers[id] = { value: "", other: "" };
    return answers;
  }, {} as QuestionnaireAnswers);

export const emptyContactDetails: ContactDetails = {
  name: "",
  age: "",
  email: "",
  whatsapp: "",
  instagram: "",
};
